/**
 * Table data for the one-time switch (PortfolioTradeV2 / SwitchSection).
 *
 * Uses shared switch column definitions from shared/switchTableColumns.js
 * and adds switch-specific columns (quantity) and behaviors (sell-all, distribute).
 */
import React from "react";
import _ from "lodash";

import { makeStyles } from "@material-ui/core";

import {
  QtyNumberFormat,
  _SwitchSellAllCheckWrapper,
} from "../../../../../TradeStep/components/PortfolioTrade/table-data";
import { CurrencyNumberFormat } from "../../../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import { PercentageNumberFormat } from "../../../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import { SellAllBody } from "../../table-data";
import { getNumberValue, transactionValueToQuantity, percentageToQuantity } from "../../../../../../utils";
import { TRANSACTION_INSTR } from "../../../../../../constants";
import { withEuroOrDash, formatQty } from "../../../../../../../../utils/utils";
import SwitchQuantityField from "./SwitchQuantityField";
import {getAssetInternalId} from "../../../../../../../CustomerDashboard/utils";

import {
  isSwitchOut,
  isSwitchInHeader,
  SwitchPositionCol,
  SwitchSustainabilityCol,
  SwitchSRRICol,
  SwitchBrokerFeeCol,
  SwitchDiscountCol,
  SwitchActionsCol,
  createSwitchNameCol,
  createSwitchAmountEuroCol,
  createSwitchPercentageCol,
  switchInHeaderDistribute,
} from "../../../shared/switchTableColumns";

// Re-export for consumers that already import from here
export { SWITCH_ROW_TYPE, buildUnifiedSwitchDataSource } from "../../../shared/switchTableColumns";

const useCellStyles = makeStyles(() => ({
  sellAllCheckbox: {
    padding: 4,
  },
}));

const buildInputProps = (item, options, inputComponent) => {
  const isAmount = item && item.transaction_type === TRANSACTION_INSTR.amount;
  const props = { allowNegative: false, fixedDecimalScale: isAmount };
  if (inputComponent === QtyNumberFormat) {
    props.suffix = "";
    props.decimalScale = options.maxQtyDecimals;
    if (item && !item.data.is_fund) {
      props.decimalSeparator = false;
    }
  }
  return props;
};

const isInputDisabled = (item) => item.sell_all || item.action === "delete";

const SwitchOutInput = ({ item, options, inputComponent, value, onChange, error, valueToQuantity, currentValueLabel }) => {
  const sellAllOptions = React.useMemo(() => ({
    ...options,
    existingSellTransaction: options.existingSellTransactions && options.existingSellTransactions[getAssetInternalId(item.data)],
  }), [options, item]);
  const sellAllGuard = _SwitchSellAllCheckWrapper(item, sellAllOptions, onChange, valueToQuantity);

  return (
    <SwitchQuantityField
      value={value}
      onChange={(val) => sellAllGuard(item, val)}
      error={error}
      inputComponent={inputComponent}
      inputProps={buildInputProps(item, options, inputComponent)}
      disabled={isInputDisabled(item)}
      currentValueLabel={currentValueLabel}
    />
  );
};

const SwitchInInput = ({ item, options, inputComponent, value, onChange, error }) => (
  <SwitchQuantityField
    value={value}
    onChange={onChange}
    error={error}
    inputComponent={inputComponent}
    inputProps={buildInputProps(item, options, inputComponent)}
    disabled={isInputDisabled(item)}
  />
);

// region SWITCH-SPECIFIC COLUMNS

const SwitchNameCol = createSwitchNameCol({
  onAddClick: (item, options) =>
    options.setSwitchModalData({
      item: options.switchOutsMap[item.switchOutItemId],
      key: options.action,
    }),
  footerColSpan: 4,
});

const SwitchQuantityCol = {
  header: [() => <>Anteile<br />Tausch</>],
  body: [
    ({ item, options }) => {
      if (isSwitchOut(item)) {
        return (
          <SwitchOutInput
            item={item}
            options={options}
            inputComponent={QtyNumberFormat}
            value={getNumberValue(item.transaction_value, false)}
            onChange={options.handleTransactionValueChange}
            error={_.get(item.errors, "transaction_value")}
            currentValueLabel={formatQty(item.data.quantity, options.maxQtyDecimals)}
          />
        );
      }
      const switchOutItem = options.switchOutsMap[item.switchOutItemId];
      if (isSwitchInHeader(item)) {
        const transactionSaldo = options.getTransactionSaldo(switchOutItem);
        return (
          <SwitchQuantityField
            value={getNumberValue(transactionSaldo, false)}
            inputComponent={QtyNumberFormat}
            inputProps={{ suffix: "", placeholder: "", fixedDecimalScale: true, allowNegative: true, decimalScale: options.maxQtyDecimals }}
            inputRootClassName={transactionSaldo !== 0 ? "text-red" : "text-green"}
            disabled
          />
        );
      }
      // SWITCH_IN
      return (
        <SwitchInInput
          item={item}
          options={options}
          inputComponent={QtyNumberFormat}
          value={getNumberValue(item.transaction_value, false)}
          onChange={(value) => options.handleTransactionValueChange(item, value, switchOutItem)}
          error={_.get(item.errors, "transaction_value")}
        />
      );
    },
  ],
  footer: [() => "Gesamt"],
  cellClassName: "switch-quantity-cell with-input",
  align: "right",
};

const SwitchAmountEuroCol = createSwitchAmountEuroCol({
  switchOut: (item, options) => (
    <SwitchOutInput
      item={item}
      options={options}
      inputComponent={CurrencyNumberFormat}
      value={getNumberValue(_.get(item, "calculated.transaction_value_euro"))}
      onChange={options.handleSwitchAmountEuroChanged}
      error={_.get(item.errors, "transaction_value")}
      valueToQuantity={transactionValueToQuantity}
      currentValueLabel={withEuroOrDash(item.data.market_value)}
    />
  ),
  switchInHeader: switchInHeaderDistribute,
});

const SwitchPercentageCol = createSwitchPercentageCol({
  switchOut: (item, options) => {
    const classes = useCellStyles();
    return (
      <>
        <SwitchOutInput
          item={item}
          options={options}
          inputComponent={PercentageNumberFormat}
          value={getNumberValue(_.get(item, "calculated.transaction_value_percentage"))}
          onChange={options.handleSwitchPercentageChanged}
          error={_.get(item.errors, "")}
          valueToQuantity={percentageToQuantity}
        />
        <SellAllBody item={item} options={options} customClasses={{
          radio: classes.sellAllCheckbox
        }}/>
      </>
    );
  },
});

// endregion

export const SWITCH_TABLE_STRUCTURE = {
  real: [
    SwitchPositionCol,
    SwitchNameCol,
    SwitchSustainabilityCol,
    SwitchSRRICol,
    SwitchQuantityCol,
    SwitchAmountEuroCol,
    SwitchPercentageCol,
    SwitchBrokerFeeCol,
    SwitchDiscountCol,
    SwitchActionsCol,
  ],
};
